import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import SiteFooter from '../components/SiteFooter';
import ClipGrid from '../components/ClipGrid';
import LoadingSpinner from '../components/LoadingSpinner';
import { request } from '../services/api';
import { formatNumber } from '../utils/format';

export default function PublicProfilePage() {
  const { username } = useParams();
  const navigate = useNavigate();
  
  const [profile, setProfile] = useState(null);
  const [stats, setStats] = useState(null);
  const [badges, setBadges] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;

    async function loadProfile() {
      try {
        setLoading(true);
        setError(null);
        
        // Fetch public profile (use /api/public alias to avoid dev-server routing collisions)
        const profileRes = await request(`/api/public/u/${username}`);
        if (!mounted) return;
        
        setProfile(profileRes.profile);

        // If backend returned public stats, use them
        if (profileRes.stats) {
          setStats(profileRes.stats);
        }

        // If backend returned earned badges, use them
        /* c8 ignore start */
        if (profileRes.badges) {
          setBadges(profileRes.badges || []);
        }
        /* c8 ignore stop */
      /* c8 ignore start */
      } catch (err) {
        if (!mounted) return;
        const message = err.message || 'Failed to load profile';
        setError(message);
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
      /* c8 ignore stop */
    }

    loadProfile();
    return () => { mounted = false; };
  }, [username]);

  if (loading) {
    return (
      <div className="min-h-screen bg-dark-900">
        <Header />
        <div className="flex items-center justify-center min-h-[60vh]">
          <LoadingSpinner />
        </div>
        <SiteFooter />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-dark-900">
        <Header />
        <div className="container mx-auto px-4 py-16">
          <div className="text-center">
            <h1 className="text-3xl font-bold text-white mb-4">Profile Not Found</h1>
            <p className="text-gray-400 mb-8">{error}</p>
            <button
              onClick={() => navigate('/dashboard')}
              className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition"
            >
              Back to Dashboard
            </button>
          </div>
        </div>
        <SiteFooter />
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-dark-900">
        <Header />
        <div className="container mx-auto px-4 py-16">
          <div className="text-center">
            <h1 className="text-3xl font-bold text-white mb-4">Profile Not Found</h1>
            <p className="text-gray-400 mb-8">This user's profile is not publicly visible.</p>
            <button
              onClick={() => navigate('/dashboard')}
              className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition"
            >
              Back to Dashboard
            </button>
          </div>
        </div>
        <SiteFooter />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-dark-900 flex flex-col">
      <Header />
      
      <main className="flex-1 container mx-auto px-4 py-12">
        {/* Profile Header */}
        <div className="bg-dark-800 rounded-lg p-8 mb-12 border border-dark-700">
            <div className="flex items-start gap-8">
              <div className="flex-shrink-0">
                {(profile.profile_image_url || profile.profileImageUrl) ? (
                  <img
                    src={profile.profile_image_url || profile.profileImageUrl}
                    alt={profile.display_name || profile.displayName || profile.username}
                    className="w-24 h-24 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-24 h-24 rounded-full bg-dark-700 flex items-center justify-center text-gray-400">@
                  </div>
                )}
              </div>
              <div className="flex-1">
                <h1 className="text-4xl font-bold text-white mb-2">
                  {profile.display_name || profile.displayName || profile.username}
                </h1>
                <p className="text-gray-400 text-lg mb-4">@{profile.username}</p>
                {profile.created_at && (
                  <p className="text-gray-500 text-sm mb-4">Joined {new Date(profile.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
                )}
              
              {/* Twitch link */}
              { (profile.twitch_username || profile.twitchUsername) && (
                <div className="mt-2">
                  <a
                    href={`https://twitch.tv/${profile.twitch_username || profile.twitchUsername}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-purple-700 hover:bg-purple-600 text-white rounded-lg"
                  >
                    View on Twitch
                  </a>
                </div>
              )}
              
              {/* Badges (moved below header) */}
            </div>
          </div>
        </div>

        {/* Badges - prominent section placed between header and stats */}
        {badges && badges.length > 0 && (
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-white mb-4">Achievements</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
              {badges.map((badge) => (
                <div key={badge.id} className="flex items-center gap-4 bg-dark-800 border border-dark-700 rounded-xl p-4">
                  {badge.iconUrl && (
                    <img src={badge.iconUrl} alt={badge.name} className="w-24 h-24 object-contain rounded-md shadow-lg" />
                  )}
                  <div>
                    <div className="text-white text-lg font-semibold">{badge.name}</div>
                    <div className="text-gray-400 text-sm mt-1">{badge.description}</div>
                    {badge.earnedAt && <div className="text-gray-500 text-xs mt-2">Earned {new Date(badge.earnedAt).toLocaleDateString()}</div>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-12">
            <div className="bg-dark-800 rounded-lg p-6 border border-dark-700">
              <div className="flex flex-col">
                <p className="text-gray-400 text-sm mb-2">Clips created and added to Clipto</p>
              </div>
              <p className="text-3xl font-bold text-white">
                {formatNumber(stats.clipsAsCreator || 0)}
              </p>
            </div>
            
            <div className="bg-dark-800 rounded-lg p-6 border border-dark-700">
              <p className="text-gray-400 text-sm mb-2">Clips Collected</p>
              <p className="text-3xl font-bold text-white">
                {formatNumber(stats.clipsInCollections || stats.totalClips || 0)}
              </p>
            </div>
            
            <div className="bg-dark-800 rounded-lg p-6 border border-dark-700">
              <p className="text-gray-400 text-sm mb-2">Clips Curated</p>
              <p className="text-3xl font-bold text-white">
                {formatNumber(stats.clipsCuratedWithTags || 0)}
              </p>
            </div>
            
            <div className="bg-dark-800 rounded-lg p-6 border border-dark-700">
              <p className="text-gray-400 text-sm mb-2">Unique Tags</p>
              <p className="text-3xl font-bold text-white">
                {stats.uniqueTags?.length || 0}
              </p>
            </div>
          </div>
        )}

        {/* Top Clips removed: no longer syncing views with Twitch */}

        {/* Favorite Channels removed — do not expose user preferences publicly */}
      </main>

      <SiteFooter />
    </div>
  );
}
